"use client";

import { createBrowserSupabase } from "@/lib/supabase/browser";
import { withStorePrefix } from "@/lib/supabase/storagePaths";
import { normalizeCourierCode, normalizeServiceCode } from "@/lib/expeditions";
import type { Expedition, ExpeditionDraft } from "@/lib/expeditions";

type ExpeditionRow = {
  id: string;
  name: string;
  is_active: boolean;
  sort_order: number | null;
  created_at: string | null;
  biteship_courier_code: string | null;
  icon_url?: string | null;
  biteship_service_code?: string | null;
};

function mapRow(row: ExpeditionRow): Expedition {
  return {
    id: row.id,
    name: row.name,
    isActive: row.is_active,
    sortOrder: Number(row.sort_order ?? 0),
    createdAt: row.created_at,
    biteshipCourierCode: row.biteship_courier_code,
    biteshipServiceCode: row.biteship_service_code ?? null,
    iconUrl: row.icon_url ?? null,
  };
}

/** Kolom yang selalu ada sejak mig 3720 — dasar semua jalur mundur. */
const SELECT_BASE =
  "id, name, is_active, sort_order, created_at, biteship_courier_code";

/**
 * Daftar kolom dari yang PALING LENGKAP ke yang paling tua, dicoba berurutan.
 *
 * `icon_url` (mig 3890) dan `biteship_service_code` (mig 3950) masing-masing
 * baru ada setelah migrasinya diterapkan lewat jendela runbook, sementara kode
 * tayang lebih dulu lewat auto-deploy. Di sela itu PostgREST membalas 42703
 * (kolom tak dikenal) dan menolak SELURUH query — bukan hanya kolomnya —
 * sehingga daftar ekspedisi mati total, termasuk di dropdown pengiriman yang
 * tak ada hubungannya dengan logo maupun layanan.
 *
 * Bertingkat, bukan "lengkap → paling tua": kalau 3890 sudah naik dan 3900
 * belum, mundur langsung ke `SELECT_BASE` akan ikut membuang logo yang
 * sebenarnya ADA di sana.
 *
 * Ini JARING untuk jendela deploy, bukan izin membalik urutannya: runbook tetap
 * migrasi dulu, baru merge.
 */
const SELECT_LEVELS: readonly string[] = [
  `${SELECT_BASE}, icon_url, biteship_service_code`, // mig 3950
  `${SELECT_BASE}, icon_url`, // mig 3890
  SELECT_BASE, // mig 3720
] as const;

/** Kolom termuda — hanya untuk kolom-kolom inilah jalur mundur berlaku. */
const YOUNG_COLUMNS = ["icon_url", "biteship_service_code"] as const;

type PostgrestErrorLike = { code?: string; message?: string } | null;

/**
 * `true` bila error-nya "kolom tak dikenal" untuk salah satu kolom termuda.
 *
 * Idiom + alasan persis `isMissingBrandColumnError` (`data.ts`, mig 3440).
 * Pesannya disaring ke nama kolomnya supaya error kolom LAIN tidak ikut
 * "disembuhkan" diam-diam oleh jalur mundur.
 */
function isMissingYoungColumnError(error: unknown): boolean {
  if (!error) return false;
  const e = error as { code?: string; message?: string };
  if (e.code === "42703") return true;
  const msg = e.message ?? "";
  return YOUNG_COLUMNS.some((c) => msg.includes(c));
}

/** Pesan yang dilihat admin bila mig 3950 belum naik tapi ia memetakan layanan. */
const SERVICE_COLUMN_MISSING =
  "Kolom kode layanan belum ada di database — migrasi 3950 belum diterapkan.";

type SelectResult = { data: unknown; error: PostgrestErrorLike };

/**
 * Jalankan `run` dengan daftar kolom terlengkap yang diterima server, mundur
 * setingkat tiap kali PostgREST menolak kolom termuda.
 */
async function selectWithFallback(
  run: (columns: string) => PromiseLike<SelectResult>
): Promise<Expedition[]> {
  for (let level = 0; level < SELECT_LEVELS.length; level += 1) {
    const { data, error } = await run(SELECT_LEVELS[level]);
    if (!error) {
      return (data as ExpeditionRow[] | null)?.map(mapRow) ?? [];
    }
    const isLast = level === SELECT_LEVELS.length - 1;
    if (isLast || !isMissingYoungColumnError(error)) {
      throw new Error(error.message ?? "Gagal memuat ekspedisi.");
    }
  }
  return [];
}

/**
 * Mutasi dengan payload LENGKAP; bila server menolak kolom termuda (42703),
 * ulangi dengan payload yang sudah dikurangi.
 *
 * `strict` = payload lengkap membawa nilai yang BERARTI pada kolom yang hilang
 * itu (mis. admin baru saja memilih sebuah layanan). Menurunkannya diam-diam
 * di situ berarti layar berkata "tersimpan" untuk sesuatu yang tak tersimpan
 * di mana pun — jadi yang benar adalah gagal dengan pesan yang menyebut
 * migrasinya, bukan berhasil palsu.
 */
async function writeWithFallback(
  run: (payload: Record<string, unknown>) => PromiseLike<{ error: PostgrestErrorLike }>,
  full: Record<string, unknown>,
  reduced: Record<string, unknown>,
  strict: boolean
): Promise<void> {
  const first = await run(full);
  if (!first.error) return;
  if (!isMissingYoungColumnError(first.error)) {
    throw new Error(first.error.message ?? "Gagal menyimpan ekspedisi.");
  }
  if (strict) throw new Error(SERVICE_COLUMN_MISSING);

  const second = await run(reduced);
  if (second.error) {
    throw new Error(second.error.message ?? "Gagal menyimpan ekspedisi.");
  }
}

/** Daftar semua ekspedisi (admin melihat aktif & nonaktif), urut tampil. */
export async function fetchExpeditions(): Promise<Expedition[]> {
  const db = createBrowserSupabase();
  if (!db) return [];

  return selectWithFallback((columns) =>
    db
      .from("expeditions")
      .select(columns)
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true })
  );
}

/**
 * Ekspedisi AKTIF saja — dipakai dropdown pilihan pengiriman (admin & pembeli).
 * Butuh sesi LOGIN: SELECT anon pada `expeditions` sudah dicabut (bughunt
 * A3), jadi jangan memanggilnya dari halaman yang terbuka tanpa login.
 */
export async function fetchActiveExpeditions(): Promise<Expedition[]> {
  const db = createBrowserSupabase();
  if (!db) return [];

  return selectWithFallback((columns) =>
    db
      .from("expeditions")
      .select(columns)
      .eq("is_active", true)
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true })
  );
}

/** Simpan ekspedisi (insert bila `id` kosong, update bila ada). */
export async function saveExpedition(
  draft: ExpeditionDraft,
  id?: string
): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");

  const courier = normalizeCourierCode(draft.biteshipCourierCode);
  // Layanan tak pernah berdiri sendiri: tanpa kurirnya ia tak menunjuk apa
  // pun, dan baris yang dipindahkan ke kategori Manual (kurir dikosongkan)
  // harus ikut melepaskannya.
  const service = courier === null ? null : normalizeServiceCode(draft.biteshipServiceCode);

  const base = {
    name: draft.name.trim(),
    is_active: draft.isActive,
    biteship_courier_code: courier,
  };
  const full = { ...base, biteship_service_code: service };

  await writeWithFallback(
    (payload) =>
      id
        ? db.from("expeditions").update(payload).eq("id", id)
        : db.from("expeditions").insert(payload),
    full,
    base,
    service !== null
  );
}

/** Aktif/nonaktifkan ekspedisi tanpa menghapusnya. */
export async function setExpeditionActive(
  id: string,
  isActive: boolean
): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");

  const { error } = await db
    .from("expeditions")
    .update({ is_active: isActive })
    .eq("id", id);
  if (error) throw new Error(error.message);
}

/**
 * Pasang/lepas kode kurir Biteship pada satu ekspedisi, tanpa menyentuh field
 * lain. Dipakai dropdown per-baris di halaman Pengaturan Ekspedisi — pola
 * cermin `setExpeditionActive`, supaya admin tak perlu membuka form edit hanya
 * untuk memetakan satu kurir.
 */
export async function setExpeditionCourierCode(
  id: string,
  code: string | null
): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");

  const courier = normalizeCourierCode(code);
  // ⚠️ Kode LAYANAN selalu ikut dikosongkan. Daftar layanan milik kurir yang
  // BARU, jadi "reg_pack" milik Lion yang tertinggal pada baris yang kini
  // dipetakan ke TIKI akan cocok dengan layanan `reg`… yang bukan itu. Bug
  // yang sama persis dengan yang mig 3950 perbaiki, hanya lewat pintu lain.
  await writeWithFallback(
    (payload) => db.from("expeditions").update(payload).eq("id", id),
    { biteship_courier_code: courier, biteship_service_code: null },
    { biteship_courier_code: courier },
    // Nilai yang hilang di jalur mundur adalah `null` — persis keadaan tabel
    // sebelum mig 3950. Tak ada yang tersembunyi, jadi tak perlu gagal.
    false
  );
}

/**
 * Pasang/lepas kode LAYANAN Biteship pada satu ekspedisi (mig 3950), tanpa
 * menyentuh field lain — pola cermin `setExpeditionCourierCode`.
 *
 * `null` = baris ini kembali mewakili kurirnya secara umum. Dipanggil dropdown
 * layanan per-baris di Pengaturan Ekspedisi; kurirnya sendiri tidak ikut
 * ditulis, karena mengganti kurir adalah aksi lain yang justru MENGOSONGKAN
 * kolom ini.
 *
 * TIDAK punya jalur mundur: satu-satunya kolom yang ditulisnya adalah kolom
 * yang mungkin belum ada, jadi "berhasil tanpa menulis apa pun" adalah
 * kebohongan yang paling mahal di sini — admin mengira pemetaannya beres.
 */
export async function setExpeditionServiceCode(
  id: string,
  serviceCode: string | null
): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");

  const { error } = await db
    .from("expeditions")
    .update({ biteship_service_code: normalizeServiceCode(serviceCode) })
    .eq("id", id);
  if (error) {
    throw new Error(
      isMissingYoungColumnError(error) ? SERVICE_COLUMN_MISSING : error.message
    );
  }
}

/**
 * Pasang/lepas LOGO khusus toko pada satu ekspedisi, tanpa menyentuh field
 * lain — pola cermin `setExpeditionCourierCode`.
 *
 * `null` = lepas timpaan, sehingga barisnya kembali memakai logo default
 * platform (`biteship_couriers.icon_url`) lewat kode kurirnya. Karena itu
 * "hapus logo" di layar admin TIDAK berarti "tanpa logo": itu keputusan yang
 * dijaga `resolveExpeditionLogo`, bukan di sini.
 *
 * Berkas lamanya sengaja TIDAK dihapus dari Storage. Path-nya ber-cap waktu
 * (`expedition-icons/<ts>.<ext>`) sehingga tak pernah ditimpa, dan menghapus
 * duluan berarti kehilangan gambar bila update barisnya gagal — persis pola
 * ikon merek/kategori. Sampahnya diurus penyapu file yatim, yang sejak mig
 * 3890 tahu kolom ini.
 */
export async function setExpeditionIconUrl(
  id: string,
  iconUrl: string | null
): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");

  const { error } = await db
    .from("expeditions")
    .update({ icon_url: iconUrl?.trim() || null })
    .eq("id", id);
  if (error) throw new Error(error.message);
}

/**
 * Unggah logo ekspedisi ke bucket publik `product-media` → URL publik.
 * Cermin `uploadBrandIconToSupabase` (`data.ts`), termasuk `withStorePrefix`
 * (mig 3120) supaya objeknya jatuh ke ruang tokonya sendiri.
 *
 * ⚠️ Ini jalur PER-TOKO. Logo DEFAULT tingkat platform TIDAK boleh lewat sini
 * — ia akan mendarat di ruang salah satu tenant dan ikut terhapus saat toko
 * itu di-reset/restore. Jalurnya sendiri: `/api/platform/courier-logo`.
 */
export async function uploadExpeditionIconToSupabase(file: File): Promise<string> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Tidak dapat terhubung.");
  const ext = file.name.split(".").pop() ?? "jpg";
  const path = await withStorePrefix(db, `expedition-icons/${Date.now()}.${ext}`);
  const { error } = await db.storage.from("product-media").upload(path, file, {
    upsert: false,
    contentType: file.type,
    cacheControl: "31536000",
  });
  if (error) throw new Error(error.message);
  const { data } = db.storage.from("product-media").getPublicUrl(path);
  return data.publicUrl;
}

/**
 * Simpan urutan tampil ekspedisi: tulis `sort_order` tiap baris = posisi
 * indeksnya di `orderedIds`. Dipakai tombol naik/turun di halaman admin.
 */
export async function persistExpeditionOrder(
  orderedIds: string[]
): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");

  const results = await Promise.all(
    orderedIds.map((id, index) =>
      db.from("expeditions").update({ sort_order: index }).eq("id", id)
    )
  );
  const failed = results.find((r) => r.error);
  if (failed?.error) throw new Error(failed.error.message);
}

/** Hapus ekspedisi. */
export async function deleteExpedition(id: string): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");

  const { error } = await db.from("expeditions").delete().eq("id", id);
  if (error) throw new Error(error.message);
}
